import { FC, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import ComingSoon from 'components/ComingSoon/ComingSoon';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiAppointmentMetricBreakdown from 'hooks/useRoiAppointmentMetricBreakdown';
import { METRIC_KEYS, METRIC_OPTIONS } from './METRIC_OPTIONS';
import MetricDonutChart from './MetricDonutChart';
import MetricProductionChart from './MetricProductionChart';
import MetricBreakdownTable from './MetricBreakdownTable';
import SourceDropdown, { SOURCE_OPTIONS } from './SourceDropdown';

interface AppointmentMetricBreakdownProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const AppointmentMetricBreakdown: FC<AppointmentMetricBreakdownProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [selectedMetric, setSelectedMetric] = useState(METRIC_OPTIONS[0]);
  const [selectedSource, setSelectedSource] = useState(SOURCE_OPTIONS[0]);

  const { data, isLoading, isValidating } = useRoiAppointmentMetricBreakdown({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    metric: selectedMetric.key,
    source: selectedSource.key,
    clinicIds,
  });

  const isProduction = selectedMetric.key === METRIC_KEYS.PRODUCTION;
  const isShows = selectedMetric.key === METRIC_KEYS.SHOWS;
  const periodData = data?.periodData ?? { total: 0, details: [], rows: [] };
  const total = periodData.total;

  return (
    <div className="px-3.2 py-2.4 shadow-elevation-06-hover bg-white min-h-[44rem] flex flex-col gap-y-2.4">
      <div className="flex items-center gap-x-2.4">
        <Listbox value={selectedMetric} onChange={setSelectedMetric}>
          {({ open }) => (
            <div className="relative">
              <Listbox.Button className="flex items-center gap-x-0.8 cursor-pointer outline-none">
                <span className="text-16 font-bold leading-[2.4rem]">
                  {selectedMetric.label}
                </span>
                {!isShows && (
                  <span className="text-16 leading-[2.4rem] text-magenta">
                    ({isProduction ? '$' : ''}
                    {total.toLocaleString()})
                  </span>
                )}
                <ChevronDownIcon className="w-1.4 h-1.4" />
              </Listbox.Button>
              {open && (
                <Listbox.Options className="absolute mt-0.5 bg-white rounded-[1rem] shadow-primary z-10 py-1.5 flex flex-col gap-y-1.5 w-max outline-none max-h-[30rem] overflow-y-auto">
                  {METRIC_OPTIONS.map((option) => (
                    <Listbox.Option
                      key={option.key}
                      value={option}
                      className={({ active, selected }) =>
                        `cursor-pointer pl-1.6 pr-2.4 ${
                          active || selected ? 'text-magenta' : ''
                        }`
                      }
                    >
                      {option.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              )}
            </div>
          )}
        </Listbox>
        <SourceDropdown value={selectedSource} onChange={setSelectedSource} />
      </div>

      {isShows ? (
        <div className="flex-1 flex items-center justify-center">
          <ComingSoon logoClassName="opacity-[30%] w-[20rem] h-[5rem] mb-2.4" />
        </div>
      ) : isLoading || isValidating ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <>
          {isProduction ? (
            <MetricProductionChart details={periodData.details} />
          ) : (
            <MetricDonutChart
              details={periodData.details}
              unitLabel={selectedMetric.unitLabel}
            />
          )}

          <MetricBreakdownTable
            columns={selectedMetric.columns}
            rows={periodData.rows}
          />
        </>
      )}
    </div>
  );
};

export default AppointmentMetricBreakdown;
